import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Reveal } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Empty, Field, PageHead, Panel, Skeleton, useToast } from '../components/ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { relative } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';

interface Device {
  id: string;
  name: string;
  kind: 'fingerspot' | 'face_kiosk' | 'qr_kiosk' | 'door_lock';
  office: string;
  serialNo: string | null;
  cloudId: string | null;
  active: boolean;
  countsForAttendance: boolean;
  lastSeenAt: string | null;
  transport: 'cloud' | 'easylink' | null;
  enrolled: number;
  unmatchedPunches: number;
}

const KIND: Record<Device['kind'], { label: string; icon: 'fingerprint' | 'face' | 'search' | 'lock' }> = {
  fingerspot: { label: 'Fingerspot', icon: 'fingerprint' },
  face_kiosk: { label: 'Face kiosk', icon: 'face' },
  qr_kiosk: { label: 'QR kiosk', icon: 'search' },
  door_lock: { label: 'Door lock', icon: 'lock' },
};

export function Devices() {
  const q = useQuery({ queryKey: ['devices'], queryFn: () => get<Device[]>('/api/devices') });
  const admin = !!useMe().data?.access.devices;
  const unmatched = useQuery({ queryKey: ['unmatched'], queryFn: () => get<{ pin: string; deviceId: string; n: number; last: string }[]>('/api/devices/unmatched') });
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [office, setOffice] = useState('');
  const offices = useMemo(() => [...new Set(q.data?.map((d) => d.office))].sort((a, b) => a.localeCompare(b)), [q.data]);
  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return (q.data ?? []).filter((d) => {
      if (office && d.office !== office) return false;
      const text = [d.name, d.office, d.serialNo, d.cloudId, KIND[d.kind].label].join(' ').toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [q.data, search, office]);
  const isKiosk = !!secret?.includes('/kiosk#');
  const qc = useQueryClient();
  const rotate = useMutation({
    mutationFn: (id: string) => post<{ webhookPath?: string; kioskPath?: string }>(`/api/devices/${id}/rotate-secret`),
    onSuccess: (r) => setSecret(`${location.origin}${r.webhookPath ?? r.kioskPath}`),
  });
  return (
    <div>
      <PageHead
        eyebrow={`Devices · ${q.data?.length ?? '…'}`}
        title={
          <>
            Where punches <em>come from.</em>
          </>
        }
        lede="Fingerspot is now one capture source among several. Each device gets its own secret webhook URL; the old open endpoint is retired. Open a machine to manage who is on it."
        aside={
          admin ? (
            <Button variant="primary" icon="plus" onClick={() => setOpen(true)}>
              Add device
            </Button>
          ) : null
        }
      />
      {q.data?.length ? (
        <div className="toolbar">
          <div className="input-group">
            <Icon name="search" />
            <input className="input" type="search" placeholder="Search by name, serial or cloud ID…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search devices" />
          </div>
          {offices.length > 1 ? (
            <select className="select" style={{ width: 'auto' }} value={office} onChange={(e) => setOffice(e.target.value)} aria-label="Office">
              <option value="">All offices</option>
              {offices.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : null}
          {search || office ? (
            <span className="muted" style={{ fontSize: 'var(--step--1)' }} aria-live="polite">
              {shown.length} of {q.data.length}
            </span>
          ) : null}
        </div>
      ) : null}
      <Reveal className="stack">
        {q.isLoading ? (
          <Skeleton h={220} />
        ) : !q.data?.length ? (
          <Empty title="No devices yet." />
        ) : !shown.length ? (
          <Empty
            title="No devices match."
            action={
              <Button size="sm" onClick={() => { setSearch(''); setOffice(''); }}>
                Clear search
              </Button>
            }
          />
        ) : (
          <Reveal.Item className="grid-12">
            {shown.map((d) => {
              const online = d.lastSeenAt && Date.now() - Date.parse(d.lastSeenAt) < 2 * 3600_000;
              return (
                <div key={d.id} className="col-4 panel panel--interactive" style={{ padding: 22, display: 'grid', gap: 14 }}>
                  <div className="row row--between">
                    <span style={{ width: 40, height: 40, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'var(--bg-3)', color: online ? 'var(--ok)' : 'var(--ink-3)' }}>
                      <Icon name={KIND[d.kind].icon} width={20} height={20} />
                    </span>
                    <Chip tone={online ? 'ok' : d.lastSeenAt ? 'bad' : 'wait'} live={!!online}>
                      {online ? 'Online' : d.lastSeenAt ? `Seen ${relative(d.lastSeenAt)}` : 'Never seen'}
                    </Chip>
                  </div>
                  <div>
                    <div className="display" style={{ fontSize: 'var(--step-1)' }}>
                      {d.name}
                    </div>
                    <div className="faint" style={{ fontSize: 'var(--step--1)' }}>
                      {KIND[d.kind].label} · {d.office}
                    </div>
                  </div>
                  <dl className="dl" style={{ gridTemplateColumns: '110px 1fr' }}>
                    <dt>Serial</dt>
                    <dd className="mono">{d.serialNo ?? '—'}</dd>
                    <dt>Cloud ID</dt>
                    <dd className="mono">{d.cloudId ?? '—'}</dd>
                    <dt>Enrolled</dt>
                    <dd>{d.enrolled}</dd>
                    <dt>Unmatched</dt>
                    <dd style={{ color: d.unmatchedPunches ? 'var(--ochre)' : undefined }}>{d.unmatchedPunches} punches</dd>
                  </dl>
                  <div className="row row--wrap" style={{ gap: 8 }}>
                    {d.kind === 'fingerspot' || d.kind === 'door_lock' ? (
                      <Link to="/devices/$id" params={{ id: d.id }}>
                        <Button size="sm" variant={d.transport ? 'primary' : 'default'} iconRight="arrowRight">
                          People on it
                        </Button>
                      </Link>
                    ) : null}
                    {admin && (d.kind === 'fingerspot' || d.kind === 'face_kiosk' || d.kind === 'qr_kiosk') ? (
                      <Button size="sm" icon="key" loading={rotate.isPending && rotate.variables === d.id} onClick={() => rotate.mutate(d.id)}>
                        {d.kind === 'fingerspot' ? 'New webhook URL' : 'New pairing link'}
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </Reveal.Item>
        )}
        {unmatched.data?.length ? (
          <Reveal.Item>
            <Panel eyebrow="Needs mapping" title="Punches from unknown PINs">
              <p className="muted" style={{ fontSize: 'var(--step--1)', marginBottom: 12 }}>
                These scans are kept, not dropped. Link the PIN to an employee and their days are rebuilt.
              </p>
              <table className="table">
                <thead>
                  <tr>
                    <th>PIN</th>
                    <th>Device</th>
                    <th className="right">Punches</th>
                    <th className="right">Last</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatched.data.map((u) => (
                    <tr key={`${u.deviceId}-${u.pin}`}>
                      <td className="mono">{u.pin}</td>
                      <td className="muted">{q.data?.find((d) => d.id === u.deviceId)?.name ?? '—'}</td>
                      <td className="right num">{u.n}</td>
                      <td className="right faint">{relative(u.last)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </Reveal.Item>
        ) : null}
      </Reveal>
      <AddDevice open={open} onClose={() => setOpen(false)} onCreated={(url) => { setSecret(url); void qc.invalidateQueries({ queryKey: ['devices'] }); }} />
      <Dialog open={!!secret} onClose={() => setSecret(null)} eyebrow="Shown once" title={isKiosk ? <>Pairing <em className="serif-italic">link</em></> : <>Webhook <em className="serif-italic">URL</em></>} footer={<Button variant="primary" onClick={() => setSecret(null)}>{isKiosk ? 'Done' : 'I’ve saved it'}</Button>}>
        <div className="stack">
          <p className="muted">
            {isKiosk
              ? 'Open this link once on the kiosk tablet (Chrome, full-screen). The tablet remembers it; anyone with the link can pair a tablet as this kiosk, so don’t share it. A new link unpairs the old tablet.'
              : 'Paste this into the Fingerspot developer portal for this device. It contains the device’s secret, so treat it like a password. Hearth stores only a hash.'}
          </p>
          <input className="input input--mono" readOnly value={secret ?? ''} onFocus={(e) => e.currentTarget.select()} aria-label="Webhook URL" />
          <Button icon="file" onClick={() => void navigator.clipboard?.writeText(secret ?? '')}>
            Copy
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function AddDevice({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (url: string) => void }) {
  const lookups = useLookups();
  const toast = useToast();
  const [f, setF] = useState({ officeId: '', kind: 'fingerspot', name: '', serialNo: '', cloudId: '' });
  const m = useMutation({
    mutationFn: () => post<{ webhookPath?: string; kioskPath?: string }>('/api/devices', { ...f, officeId: f.officeId || lookups.data?.offices[0]?.id, serialNo: f.serialNo || undefined, cloudId: f.cloudId || undefined }),
    onSuccess: (r) => {
      onClose();
      toast({ title: 'Device added' });
      onCreated(`${location.origin}${r.webhookPath ?? r.kioskPath}`);
    },
  });
  return (
    <Dialog open={open} onClose={onClose} eyebrow="New device" title="Add a capture device" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>Add device</Button></>}>
      <div className="grid-12" style={{ gap: 14 }}>
        <div className="col-6">
          <Field label="Office">
            {(p) => (
              <select {...p} className="select" value={f.officeId} onChange={(e) => setF({ ...f, officeId: e.target.value })}>
                {lookups.data?.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
          </Field>
        </div>
        <div className="col-6">
          <Field label="Kind">
            {(p) => (
              <select {...p} className="select" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                <option value="fingerspot">Fingerspot</option>
                <option value="face_kiosk">Face kiosk (tablet: badge or number + face)</option>
                <option value="qr_kiosk">QR kiosk (tablet: badge only)</option>
                <option value="door_lock">Door lock (not for attendance)</option>
              </select>
            )}
          </Field>
        </div>
        <div className="col-12">
          <Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Lobby — Revo W-230N" />}</Field>
        </div>
        <div className="col-6">
          <Field label="Serial number">{(p) => <input {...p} className="input input--mono" value={f.serialNo} onChange={(e) => setF({ ...f, serialNo: e.target.value })} />}</Field>
        </div>
        <div className="col-6">
          <Field label="Cloud ID" hint="From the Fingerspot developer portal">{(p) => <input {...p} className="input input--mono" value={f.cloudId} onChange={(e) => setF({ ...f, cloudId: e.target.value })} />}</Field>
        </div>
        {m.error ? <div className="col-12"><Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Failed'}</Callout></div> : null}
      </div>
    </Dialog>
  );
}
